import React, { useState, useEffect } from 'react';
import { schoolAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import './MasterDataManagement.css';

const SchoolManagement = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingSchool, setEditingSchool] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [newSchool, setNewSchool] = useState({
    code: '',
    name: '',
    ppd: '',
    kodDun: '',
    kodPar: '',
    address1: '',
    address2: '',
    postcode: '',
    state: '',
    contact: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [ppdFilter, setPpdFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    loadSchools();
  }, [searchTerm, ppdFilter, currentPage, pageSize]);

  const loadSchools = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await schoolAPI.getSchools(searchTerm, ppdFilter);
      
      if (response.success) {
        setSchools(response.data);
        setTotalCount(response.data.length);
        setTotalPages(Math.ceil(response.data.length / pageSize));
      } else {
        setError(response.message || 'Failed to load schools');
      }
    } catch (err) {
      console.error('Load schools error:', err);
      setError('An error occurred while loading schools');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSchool = () => {
    setModalMode('create');
    setEditingSchool(null);
    setNewSchool({
      code: '',
      name: '',
      ppd: '',
      kodDun: '',
      kodPar: '',
      address1: '',
      address2: '',
      postcode: '',
      state: '',
      contact: ''
    });
    setShowModal(true);
  };

  const handleEditSchool = (school) => {
    setModalMode('edit');
    setEditingSchool(school);
    setNewSchool({
      code: school.code,
      name: school.name,
      ppd: school.ppd || '',
      kodDun: school.kodDun || '',
      kodPar: school.kodPar || '',
      address1: school.address1 || '',
      address2: school.address2 || '',
      postcode: school.postcode || '',
      state: school.state || '',
      contact: school.contact || ''
    });
    setShowModal(true);
  };

  const handleDeleteSchool = async (code) => {
    if (window.confirm(`Are you sure you want to delete school with code ${code}? This will check for related student data.`)) {
      try {
        const response = await schoolAPI.deleteSchool(code);
        
        if (response.success) {
          loadSchools();
          console.log('School deleted successfully');
        } else {
          setError(response.message || 'Failed to delete school');
        }
      } catch (err) {
        console.error('Delete error:', err);
        setError('An error occurred while deleting the school');
      }
    }
  };

  const handleManageClasses = (school) => {
    // Navigate to the class management where the school is provided
    navigate(`/school/${school.code}/classes`);
  };

  const handleSaveSchool = async () => {
    try {
      let response;
      
      if (modalMode === 'create') {
        response = await schoolAPI.createSchool({
          SchoolCode: newSchool.code,
          Name: newSchool.name,
          Ppd: newSchool.ppd,
          KodDun: newSchool.kodDun,
          KodPar: newSchool.kodPar,
          Address1: newSchool.address1,
          Address2: newSchool.address2,
          Postcode: newSchool.postcode,
          State: newSchool.state,
          Contact: newSchool.contact
        });
      } else {
        response = await schoolAPI.updateSchool(editingSchool.code, {
          SchoolCode: newSchool.code,
          Name: newSchool.name,
          Ppd: newSchool.ppd,
          KodDun: newSchool.kodDun,
          KodPar: newSchool.kodPar,
          Address1: newSchool.address1,
          Address2: newSchool.address2,
          Postcode: newSchool.postcode,
          State: newSchool.state,
          Contact: newSchool.contact
        });
      }

      if (response.success) {
        setShowModal(false);
        loadSchools();
        console.log(`${modalMode === 'create' ? 'Created' : 'Updated'} school successfully`);
      } else {
        setError(response.message || `Failed to ${modalMode} school`);
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(`An error occurred while ${modalMode === 'create' ? 'creating' : 'updating'} the school`);
    }
  };

  if (loading && !schools.length) {
    return (
      <div className="master-data-management">
        <div className="loading">Loading schools...</div>
      </div>
    );
  }

  return (
    <div className="master-data-management">
      <div className="master-data-management-header">
        <div className="header-info">
          <h2><i className="fas fa-school"></i> School Management</h2>
          <p>Manage schools and assign to PPD, DUN, Parliament</p>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="master-data-management-toolbar">
        <div className="search-section">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search schools..."
            className="search-input"
          />
          <select
            value={ppdFilter}
            onChange={(e) => setPpdFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All PPDs</option>
            {/* This would typically be populated from an API call */}
            {/* For now, using placeholder */}
          </select>
          <button onClick={loadSchools} className="btn-search">
            <i className="fas fa-search"></i> Filter
          </button>
        </div>
        <button onClick={handleCreateSchool} className="btn-create">
          <i className="fas fa-plus"></i> Add New School
        </button>
      </div>

      <div className="table-container">
        <table className="master-data-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>School Code</th>
              <th>School Name</th>
              <th>PPD</th>
              <th>DUN</th>
              <th>Parliament</th>
              <th>Address</th>
              <th>Contact</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {schools.length > 0 ? (
              schools.map((school, index) => (
                <tr key={school.code}>
                  <td>{index + 1 + (currentPage - 1) * pageSize}</td>
                  <td><strong>{school.code}</strong></td>
                  <td>{school.name}</td>
                  <td>{school.ppd || '-'}</td>
                  <td>{school.kodDun || '-'}</td>
                  <td>{school.kodPar || '-'}</td>
                  <td>
                    {[school.address1, school.address2].filter(Boolean).join(', ') || '-'}
                  </td>
                  <td>{school.contact || '-'}</td>
                  <td>
                    {school.createdDate ? new Date(school.createdDate).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="actions-column">
                    <button
                      onClick={() => handleEditSchool(school)}
                      className="btn-action btn-edit"
                      title="Edit School"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleManageClasses(school)}
                      className="btn-action btn-manage"
                      title="Manage Classes"
                    >
                      <i className="fas fa-chalkboard"></i>
                    </button>
                    <button
                      onClick={() => handleDeleteSchool(school.code)}
                      className="btn-action btn-delete"
                      title="Delete School"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="no-data">No schools found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination-section">
          <div className="pagination-info">
            Page {currentPage} of {totalPages} ({totalCount} total schools)
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="btn-page"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn-page"
            >
              Previous
            </button>
            <span className="page-input">
              <input
                type="number"
                value={currentPage}
                onChange={(e) => setCurrentPage(Math.max(1, Math.min(totalPages, Number(e.target.value))))}
                min="1"
                max={totalPages}
                className="page-number-input"
              />
              <span>of {totalPages}</span>
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="btn-page"
            >
              Last
            </button>
          </div>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setCurrentPage(1);
            }}
            className="page-size-select"
          >
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      )}

      {/* Modal for Add/Edit School */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            <div className="modal-header">
              <h3>
                {modalMode === 'create' ? 'Add New School' : `Edit School: ${newSchool.code}`}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="modal-close-btn"
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="form-section">
                <h4><i className="fas fa-school"></i> School Information</h4>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="schoolCode">School Code *</label>
                    <input
                      id="schoolCode"
                      type="text"
                      value={newSchool.code}
                      onChange={(e) => setNewSchool({ ...newSchool, code: e.target.value.trim().toUpperCase() })}
                      placeholder="Enter school code (e.g., XBA1001)"
                      maxLength={50}
                      className="form-control"
                      disabled={modalMode === 'edit'} // Don't allow changing school code after creation
                    />
                    <small className="form-help">Unique identifier for the school (cannot be changed after creation)</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="schoolName">School Name *</label>
                    <input
                      id="schoolName"
                      type="text"
                      value={newSchool.name}
                      onChange={(e) => setNewSchool({ ...newSchool, name: e.target.value.trim() })}
                      placeholder="Enter full school name"
                      maxLength={255}
                      className="form-control"
                    />
                    <small className="form-help">Full name of the school</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="schoolPpd">PPD (District) *</label>
                    <input
                      id="schoolPpd"
                      type="text"
                      value={newSchool.ppd}
                      onChange={(e) => setNewSchool({ ...newSchool, ppd: e.target.value.trim() })}
                      placeholder="Enter PPD (District Education Office)"
                      maxLength={255}
                      className="form-control"
                    />
                    <small className="form-help">Education Department (Pusat Pendidikan Daerah)</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="dunCode">DUN Code</label>
                    <input
                      id="dunCode"
                      type="text"
                      value={newSchool.kodDun}
                      onChange={(e) => setNewSchool({ ...newSchool, kodDun: e.target.value.trim() })}
                      placeholder="Enter DUN code (e.g., N01)"
                      maxLength={50}
                      className="form-control"
                    />
                    <small className="form-help">State Legislative Assembly constituency code</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="parCode">Parliament Code</label>
                    <input
                      id="parCode"
                      type="text"
                      value={newSchool.kodPar}
                      onChange={(e) => setNewSchool({ ...newSchool, kodPar: e.target.value.trim() })}
                      placeholder="Enter Parliament code (e.g., P001)"
                      maxLength={50}
                      className="form-control"
                    />
                    <small className="form-help">Parliament constituency code</small>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4><i className="fas fa-address-book"></i> Contact Information</h4>

                <div className="form-row">
                  <div className="form-group wide">
                    <label htmlFor="address1">Address Line 1</label>
                    <input
                      id="address1"
                      type="text"
                      value={newSchool.address1}
                      onChange={(e) => setNewSchool({ ...newSchool, address1: e.target.value.trim() })}
                      placeholder="Street address"
                      maxLength={500}
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group wide">
                    <label htmlFor="address2">Address Line 2</label>
                    <input
                      id="address2"
                      type="text"
                      value={newSchool.address2}
                      onChange={(e) => setNewSchool({ ...newSchool, address2: e.target.value.trim() })}
                      placeholder="City, State, etc."
                      maxLength={500}
                      className="form-control"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="postcode">Postcode</label>
                    <input
                      id="postcode"
                      type="text"
                      value={newSchool.postcode}
                      onChange={(e) => setNewSchool({ ...newSchool, postcode: e.target.value.trim() })}
                      placeholder="Postal code"
                      maxLength={20}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="state">State</label>
                    <input
                      id="state"
                      type="text"
                      value={newSchool.state}
                      onChange={(e) => setNewSchool({ ...newSchool, state: e.target.value.trim() })}
                      placeholder="State name"
                      maxLength={100}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="contact">Contact</label>
                    <input
                      id="contact"
                      type="text"
                      value={newSchool.contact}
                      onChange={(e) => setNewSchool({ ...newSchool, contact: e.target.value.trim() })}
                      placeholder="Phone number or contact person"
                      maxLength={50}
                      className="form-control"
                    />
                    <small className="form-help">Phone number or contact person for this school</small>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveSchool}
                disabled={!newSchool.code.trim() || !newSchool.name.trim() || !newSchool.ppd.trim()}
                className="btn btn-primary"
              >
                {modalMode === 'create' ? 'Create School' : 'Update School'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolManagement;